// src/proposalSections.ts
import { supabase } from "./supabaseClient";
import { durableGet, durableSet } from "./durableStore";
import { mergeLayoutOrder, orderedSections } from "./shared/sectionEditing";

// Keep these types aligned with SettingsPage
// "reviews" was dropped (2026-09-23): no section used it and the desktop
// proposal never rendered it. The DB column org_proposal_sections.reviews is
// left in place, unread and unwritten.
export type ProposalSectionType = "bullets" | "paragraph";

export type ProposalSection = {
  id: string;
  title: string;
  enabled: boolean;
  type: ProposalSectionType;
  text?: string;
};

export type ProposalSectionRow = {
  id: string;
  org_id: string;
  sort_order: number;
  title: string;
  enabled: boolean;
  /** Stored as text; anything other than "bullets" is read as "paragraph". */
  type: string;
  text: string | null;
  created_at?: string;
  updated_at?: string;
};

const cacheKey = (orgId: string) => `du_cache:org_proposal_sections::${orgId}`;
const cacheTsKey = (orgId: string) => `du_cache:org_proposal_sections_ts::${orgId}`;

// ----------
// Mappers
// ----------
function rowToSection(r: ProposalSectionRow): ProposalSection {
  return {
    id: r.id,
    title: r.title,
    enabled: !!r.enabled,
    type: r.type === "bullets" ? "bullets" : "paragraph",
    text: r.text ?? undefined,
  };
}

function sectionToUpsertRow(orgId: string, section: ProposalSection, sortOrder: number) {
  return {
    id: section.id, // can be client-generated or existing uuid
    org_id: orgId,
    sort_order: sortOrder,
    title: (section.title || "").trim(),
    enabled: !!section.enabled,
    type: section.type,
    text: section.text ?? null,
  };
}

// ----------
// Cache helpers (durable — see src/durableStore.ts for why not raw
// localStorage). Mirrors readCachedSettings/writeCachedSettings in settings.ts.
// ----------

/**
 * Last-known-good blocks for this org, or null when we have never completed a
 * successful authoritative fetch (true first run).
 */
export function readCachedProposalSections(
  orgId: string
): ProposalSection[] | null {
  if (!orgId) return null;
  try {
    const raw = durableGet(cacheKey(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ProposalSection[]) : null;
  } catch {
    return null;
  }
}

function writeCachedProposalSections(orgId: string, sections: ProposalSection[]) {
  if (!orgId) return;
  try {
    durableSet(cacheKey(orgId), JSON.stringify(sections));
    durableSet(cacheTsKey(orgId), String(Date.now()));
  } catch {}
}

/**
 * Where a set of blocks came from. Callers that can WRITE (SettingsPage) must
 * refuse to seed/overwrite unless `authoritative` is true — otherwise a flaky
 * or offline read can push a stripped-down block set back to the org.
 */
export type ProposalSectionsResult = {
  sections: ProposalSection[];
  /** True only for a confirmed, successful, non-empty-or-genuinely-empty read. */
  authoritative: boolean;
  /** True when `sections` came from the last-known-good cache. */
  fromCache: boolean;
};

// ----------
// Read (network first, cached last-known-good fallback)
//
// Guarantees, mirroring the July settings hardening in settings.ts:
//   - A FAILED fetch never returns blanks and never touches the cache.
//   - A successful-but-EMPTY read is NOT treated as proof the org has no
//     blocks: an anon / expired-JWT request that RLS filters to zero rows
//     comes back 200 with []. Previously that overwrote a populated cache
//     with [], permanently poisoning the offline fallback. We now keep the
//     cached blocks and refuse to record the empty result over them.
//   - Only a genuinely empty read with NO cached blocks records [].
// ----------
export async function fetchProposalSectionsResult(
  orgId: string
): Promise<ProposalSectionsResult> {
  if (!orgId) return { sections: [], authoritative: false, fromCache: false };

  const cached = readCachedProposalSections(orgId);

  // 1) Try Supabase first
  try {
    const { data, error } = await supabase
      .from("org_proposal_sections")
      .select("id, org_id, sort_order, title, enabled, type, text")
      .eq("org_id", orgId)
      .order("sort_order", { ascending: true });

    if (error) throw error;

    const rows = (data || []) as ProposalSectionRow[];
    const sections = rows.map(rowToSection);

    if (sections.length === 0 && cached && cached.length > 0) {
      // Empty read while we hold good blocks — distrust it. Keep the cache
      // intact (do NOT write [] over it) and serve last-known-good.
      // eslint-disable-next-line no-console
      console.warn(
        "[proposalSections] empty read ignored; serving",
        cached.length,
        "cached blocks"
      );
      return { sections: cached, authoritative: false, fromCache: true };
    }

    // Confirmed result (populated, or genuinely empty with nothing cached).
    writeCachedProposalSections(orgId, sections);
    return { sections, authoritative: true, fromCache: false };
  } catch (err) {
    // 2) Cached last-known-good. Never write on a failed fetch.
    if (cached) {
      // eslint-disable-next-line no-console
      console.warn(
        "[proposalSections] fetch failed; serving",
        cached.length,
        "cached blocks:",
        (err as any)?.message || err
      );
      return { sections: cached, authoritative: false, fromCache: true };
    }

    // 3) Nothing available — true first run with no network.
    throw err;
  }
}

/**
 * Back-compat wrapper: the render paths (ProposalPage / MobileProposalPage)
 * only need the blocks themselves.
 */
export async function fetchProposalSections(orgId: string): Promise<ProposalSection[]> {
  const { sections } = await fetchProposalSectionsResult(orgId);
  return sections;
}

// optional helper (useful later for UI messaging)
export function getProposalSectionsCacheTimestamp(orgId: string): number | null {
  try {
    const v = durableGet(cacheTsKey(orgId));
    const n = Number(v || "");
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

// ----------
// Write (admins only; enforced by RLS)
// ----------
export async function upsertProposalSections(
  orgId: string,
  sections: ProposalSection[]
): Promise<void> {
  if (!orgId) throw new Error("Missing orgId");

  const payload = (sections || []).map((s, i) => sectionToUpsertRow(orgId, s, i));

  const { error } = await supabase.from("org_proposal_sections").upsert(payload, {
    onConflict: "id",
  });

  if (error) throw error;

  // refresh cache with latest data (keeps offline accurate)
  writeCachedProposalSections(orgId, sections);
}

export async function deleteProposalSection(orgId: string, id: string): Promise<void> {
  if (!orgId) throw new Error("Missing orgId");
  if (!id) throw new Error("Missing id");

  const { error } = await supabase
    .from("org_proposal_sections")
    .delete()
    .eq("org_id", orgId)
    .eq("id", id);

  if (error) throw error;
}

// ----------
// In-place edit from the Proposal page (admins only; enforced by RLS policy
// ops_write_admins). NOT a new write path: it ends in the same
// deleteProposalSection / upsertProposalSections calls the Settings editor
// makes. What it adds is a fresh read first, so a save applies only THIS
// change on top of the server's current rows instead of writing back a stale
// local copy over another admin's edit.
// ----------
export type ProposalSectionEdit = (
  fresh: ProposalSection[],
  order: string[]
) => { sections: ProposalSection[]; order: string[]; deleteId?: string };

export async function saveProposalSectionEdit(
  orgId: string,
  layoutOrder: string[] | null | undefined,
  edit: ProposalSectionEdit
): Promise<{ sections: ProposalSection[]; order: string[] }> {
  if (!orgId) throw new Error("Missing orgId");
  const current = await fetchProposalSectionsResult(orgId);
  // A cached or unconfirmed read is not a safe base to write from.
  if (!current.authoritative) {
    throw new Error("Couldn't reach the server — change not saved.");
  }
  const baseOrder = mergeLayoutOrder(layoutOrder, current.sections);
  const next = edit(current.sections, baseOrder);
  if (next.deleteId) await deleteProposalSection(orgId, next.deleteId);
  const ordered = orderedSections(next.order, next.sections);
  await upsertProposalSections(orgId, ordered);
  return { sections: ordered, order: next.order };
}
